import { useMemo } from "react";

import type { GoogleFreeBookingLinksSettingsQuery } from "@/lib/gql/__generated__/graphql";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import { FeatureIslandContainer } from "@/components/features/_layouts";

import { GoogleFreeBookingLinksSettingsContext } from "./_context";
import { Form } from "./form";
import { Header } from "./header";
import { useGoogleFreeBookingLinksSettingsForm } from "../../hooks";

import type { GoogleFreeBookingLinksSettingsContextType } from "./_context";

type Props = {
	googleFreeBookingLinksSettingsQueryData: GoogleFreeBookingLinksSettingsQuery;
};

export const FormGoogleFreeBookingLinksIslandSettings = ({
	googleFreeBookingLinksSettingsQueryData: { googleFreeBookingLinksSettings, hpbSettings, planList, roomList },
}: Props) => {
	const form = useGoogleFreeBookingLinksSettingsForm({
		googleFreeBookingLinksSettings,
	});

	const initialContextValue: GoogleFreeBookingLinksSettingsContextType = useMemo(() => {
		const connectablePlanList = [
			...planList.plans.map((plan) => ({
				id: plan.id,
				label: plan.name,
				value: plan.planID,
				inSale: plan.inSale,
				isDayTrip: plan.isDayTrip,
			})),
			...planList.secrets.map((secret) => ({
				id: secret.id,
				label: secret.name,
				value: secret.planID,
				inSale: secret.inSale,
				isDayTrip: secret.isDayTrip,
			})),
		];
		const filteredConnectablePlanList = connectablePlanList.filter((plan) => plan.inSale && !plan.isDayTrip);

		const connectableRoomList = [
			...roomList.rooms.map((room) => ({
				id: room.id,
				label: room.name,
				value: room.roomID,
				inSale: room.inSale,
			})),
			...roomList.dummy.map((dummy) => ({
				id: dummy.id,
				label: dummy.name,
				value: dummy.roomID,
				inSale: dummy.inSale,
			})),
		];
		const filteredConnectableRoomList = connectableRoomList.filter((room) => room.inSale);

		return {
			form,
			connectablePlanList: filteredConnectablePlanList,
			connectableRoomList: filteredConnectableRoomList,
			isShowHpbGuestPageTypeOption: !!hpbSettings,
		};
	}, [form, hpbSettings, planList, roomList]);

	useTransitionBlock(form.formState.isDirty);

	return (
		<GoogleFreeBookingLinksSettingsContext.Provider value={initialContextValue}>
			<FeatureIslandContainer>
				<Header />
				<Form />
			</FeatureIslandContainer>
		</GoogleFreeBookingLinksSettingsContext.Provider>
	);
};
